import React, { useState, useEffect } from 'react';
import { X, Eye, FileText, Download, AlertCircle, Loader2 } from 'lucide-react';
import CombinedTaskViewer from './CombinedTaskViewer';
import FileViewer from './FileViewer';
import api from '../../utils/api';

/**
 * AdminTaskPreviewModal
 * 
 * Two usage modes:
 * 1. Saved tasks: pass `task` with an `id` — sections are fetched from the API.
 * 2. Unsaved/in-progress tasks (e.g. from AITaskGenerator): pass `taskData` directly
 *    with sections already included — no API call needed.
 */
const AdminTaskPreviewModal = ({ isOpen, onClose, task, taskData }) => {
    const [fullTask, setFullTask] = useState(null);
    const [loading, setLoading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            setFullTask(null);
            setPreviewFile(null);
            return;
        }

        // If taskData is provided directly (unsaved task from AI generator), use it
        if (taskData) {
            setFullTask({
                ...taskData,
                is_combined: (taskData.sections && taskData.sections.length > 0) || taskData.is_combined || taskData.task_type === 'combined'
            });
            return;
        }

        // Otherwise fetch from API using task.id
        if (task?.id) {
            fetchFullTask();
        }
    }, [isOpen, task?.id, taskData]);

    const fetchFullTask = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [taskRes, sectionsRes] = await Promise.all([
                api.get(`/tasks/${task.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                api.get(`/tasks/${task.id}/sections`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            const data = taskRes.data.task || taskRes.data;
            const sections = sectionsRes.data.sections || [];

            setFullTask({
                ...data,
                sections,
                is_combined: sections.length > 0 || data.is_combined || data.task_type === 'combined'
            });
        } catch (error) {
            console.error('Error fetching task for preview:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const displayTitle = fullTask?.title || task?.title || taskData?.title || 'Task Preview';

    return (
        <>
            <div className="fixed inset-0 z-[60] flex flex-col bg-gray-50 dark:bg-slate-800/50">
                {/* Header Bar */}
                <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-3 flex items-center justify-between flex-shrink-0 shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex items-center gap-2">
                            <Eye className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-100 text-indigo-700 dark:text-indigo-400 rounded-full text-xs font-semibold">
                                <AlertCircle className="w-3 h-3" />
                                Admin Preview
                            </span>
                        </div>
                        <span className="text-sm text-gray-400 dark:text-slate-500">|</span>
                        <h1 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                            {displayTitle}
                        </h1>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:bg-slate-700 rounded-lg transition-colors"
                    >
                        <X className="w-4 h-4" />
                        Close Preview
                    </button>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                            <p className="text-sm text-gray-500 dark:text-slate-400">Loading task preview...</p>
                        </div>
                    ) : fullTask ? (
                        <div className="max-w-4xl mx-auto px-6 py-8">
                            {/* Read-only notice */}
                            <div className="mb-6 flex items-center gap-2 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 rounded-lg px-4 py-3">
                                <Eye className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                                <p className="text-sm text-indigo-700 dark:text-indigo-400">
                                    This is a <strong>read-only preview</strong> showing how the task appears to employees. Interactions are disabled.
                                </p>
                            </div>

                            {/* Task Header */}
                            <div className="bg-white dark:bg-slate-900 shadow-sm rounded-xl p-8 mb-6 border border-gray-100 dark:border-slate-700/50">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{fullTask.title}</h2>
                                {fullTask.description && (
                                    <div className="text-gray-600 dark:text-slate-300 whitespace-pre-wrap mb-6">{fullTask.description}</div>
                                )}

                                {/* Task Metadata */}
                                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-slate-400">
                                    {fullTask.task_type && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-slate-800 rounded-full text-xs font-medium capitalize">
                                            {fullTask.task_type === 'combined' ? 'Combined Task' : fullTask.task_type}
                                        </span>
                                    )}
                                    {fullTask.phase && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium capitalize">
                                            {fullTask.phase}
                                        </span>
                                    )}
                                    {fullTask.due_days_after_assignment && (
                                        <span className="text-xs text-gray-400 dark:text-slate-500">
                                            Due: {fullTask.due_days_after_assignment} days after assignment
                                        </span>
                                    )}
                                </div>

                                {/* Attachments */}
                                {fullTask.attachments && fullTask.attachments.length > 0 && (
                                    <div className="mt-6 pt-6 border-t border-gray-100 dark:border-slate-700/50">
                                        <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-3">
                                            Attachments ({fullTask.attachments.length})
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {fullTask.attachments.map((file, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setPreviewFile(file)}
                                                    className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors group text-left"
                                                >
                                                    <div className="p-2 bg-white dark:bg-slate-900 rounded border border-gray-200 dark:border-slate-700 group-hover:border-primary/20">
                                                        <FileText className="w-4 h-4 text-gray-400 dark:text-slate-500 group-hover:text-primary" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-700 dark:text-slate-200 truncate">{file.file_name}</p>
                                                        <p className="text-xs text-gray-400 dark:text-slate-500">Click to preview</p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Combined Task Viewer (Read-only) */}
                            {fullTask.is_combined && fullTask.sections && fullTask.sections.length > 0 ? (
                                <div>
                                    <CombinedTaskViewer
                                        task={fullTask}
                                        assignment={null}
                                        previewMode={true}
                                        isAdmin={true}
                                    />
                                </div>
                            ) : (
                                /* Non-combined task content preview */
                                fullTask.content && (
                                    <div className="bg-white dark:bg-slate-900 shadow-sm rounded-xl p-8 border border-gray-100 dark:border-slate-700/50">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Task Content</h3>
                                        <div className="prose prose-sm max-w-none text-gray-600 dark:text-slate-300 whitespace-pre-wrap">
                                            {typeof fullTask.content === 'string'
                                                ? fullTask.content
                                                : JSON.stringify(fullTask.content, null, 2)
                                            }
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full gap-4">
                            <AlertCircle className="w-8 h-8 text-gray-400 dark:text-slate-500" />
                            <p className="text-sm text-gray-500 dark:text-slate-400">Failed to load task preview</p>
                        </div>
                    )}
                </div>
            </div>

            {/* File Viewer Overlay */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />
        </>
    );
};

export default AdminTaskPreviewModal;
